import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Smart scroll-restoration on route change:
 * - If URL has a hash (e.g. "/#temas"), scroll to that element after the page
 *   mounts. Re-scrolls several times because lazy-loaded content above the
 *   anchor (ranking, creator-of-month, etc.) can push it further down AFTER
 *   the initial scroll has already fired.
 * - Otherwise scroll to top.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.replace("#", ""));
      const scrollNow = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return true;
        }
        return false;
      };
      // Phase 1: try immediately, then keep retrying until the element mounts
      if (!scrollNow()) {
        let tries = 0;
        const iv = setInterval(() => {
          tries += 1;
          if (scrollNow() || tries > 30) clearInterval(iv);
        }, 100);
      }
      // Phase 2: even after scrolling, async content above the anchor
      // (fetched rankings, author grid, etc.) may push it downwards. Re-align
      // at 600ms, 1400ms and 2500ms to cover typical fetch latencies.
      const reAligns = [600, 1400, 2500].map((delay) => setTimeout(scrollNow, delay));
      return () => reAligns.forEach(clearTimeout);
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);

  return null;
}
